/**
 * Unified Permission System Types
 * Central source of truth for all permission-related types
 */

// Core permission system types
export interface CompanyPermission {
  permission_key: string;
  permission_name: string;
  description: string;
  category: string;
}

// Permission keys enum for type safety
export enum CompanyPermissionKey {
  // Owner permissions
  OWNER = 'owner',
  
  // Job management permissions
  VIEW_ALL_JOBS = 'view_all_jobs',
  VIEW_ASSIGNED_JOBS = 'view_assigned_jobs',
  CREATE_JOBS = 'create_jobs',
  EDIT_JOBS = 'edit_jobs',
  DELETE_JOBS = 'delete_jobs',
  MANAGE_JOB_STATUS = 'manage_job_status',
  
  // Application management permissions
  VIEW_APPLICATIONS = 'view_applications',
  MANAGE_APPLICATIONS = 'manage_applications',
  
  // Additional job permissions
  VIEW_JOB_LISTINGS = 'view_job_listings',
  
  // Company profile permissions
  VIEW_COMPANY_PROFILE = 'view_company_profile',
  
  // Team management permissions
  INVITE_MEMBERS = 'invite_members',
  MANAGE_MEMBER_PERMISSIONS = 'manage_member_permissions',
  VIEW_TEAM = 'view_team',
  
  // Company management permissions
  MANAGE_COMPANY_PROFILE = 'manage_company_profile',
}

// Permission preset interface for role-like groupings
export interface PermissionPreset {
  preset_key: string;
  preset_name: string;
  description: string;
  permissions: string[];
  category: 'management' | 'team' | 'basic';
  is_system_preset: boolean;
}

// Common permission presets (replacing traditional roles)
export const PERMISSION_PRESETS = {
  OWNER: 'owner_preset',
  ADMIN: 'admin_preset', 
  MANAGER: 'manager_preset',
  MEMBER: 'member_preset',
  VIEWER: 'viewer_preset'
} as const;

// Company member interface with permission preset
export interface CompanyMember {
  id: string;
  user_id: string;
  company_id: string;
  name: string;
  avatar_url?: string;
  email?: string;
  permission_preset: string;
  custom_permissions?: string[];
  joined_at?: string;
  updated_at?: string;
}

// Company invitation interface with permission preset
export interface CompanyInvitation {
  id: string;
  company_id: string;
  email: string;
  permission_preset: string;
  custom_permissions?: string[];
  invited_by: string;
  created_at: string;
  used_at?: string;
  email_sent?: boolean;
}

// Permission check utilities
export interface PermissionCheckResult {
  hasAccess: boolean;
  reason?: string;
  requiredPermissions?: string[];
}

// Permission inheritance types
export interface PermissionInheritance {
  user_id: string;
  company_id: string;
  inherited_permissions: string[];
  direct_permissions: string[];
  effective_permissions: string[];
}

// Audit trail for permission changes
export interface PermissionAuditLog {
  id: string;
  user_id: string;
  company_id: string;
  action: 'granted' | 'revoked' | 'preset_changed';
  permission_keys?: string[];
  old_preset?: string;
  new_preset?: string;
  changed_by: string;
  timestamp: string;
  reason?: string;
}